import { useState, useContext, useEffect, useCallback } from "react";
import '../../styles/page-titles.css'
import PermissionGate from "../../services/PermissionGate";
import styled from 'styled-components';
import { SidebarContext } from "../../AppRoutes";
import { getEstatisticasAbandono, getCarrinhosAbandonados } from "../../services/api";
import { getStoredUser } from '../../utils/safeStorage';
import { Doughnut, Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    ArcElement,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';
import { FaShoppingCart, FaMoneyBillWave, FaChartLine, FaPercentage, FaClock, FaWhatsapp } from 'react-icons/fa';
import { MdOutlineRemoveShoppingCart } from 'react-icons/md';
import Loading from "react-loading";
import { nowTz, formatDateTz } from '../../utils/timezone';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { formatPriceWithSymbol } from '../../utils/currency';
import { buildWhatsAppLink } from '../../utils/phone';

ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const Container = styled.div`
    display: flex;
    flex-direction: column;
    margin-left: ${({ sidebar }) => (sidebar ? '250px' : '100px')};
    min-height: 100vh;
    width: auto;
    transition: margin-left 200ms ease;
    background-color: #ffffff;
    padding: 24px;

    @media (max-width: 880px) {
        margin-left: 0;
        padding: 16px;
    }
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;
    flex-wrap: wrap;
    gap: 16px;

    .filters {
        display: flex;
        gap: 12px;
        flex-wrap: wrap;
    }

    input[type="date"] {
        padding: 8px 12px;
        border: 1px solid #ddd;
        border-radius: 8px;
        font-size: 14px;
    }

    button {
        padding: 8px 16px;
        background: linear-gradient(135deg, rgb(49, 140, 213), rgb(79, 172, 254));
        color: white;
        border: none;
        border-radius: 8px;
        cursor: pointer;
        font-weight: 500;
        transition: opacity 0.2s;

        &:hover {
            opacity: 0.9;
        }

        &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
        }
    }

    @media (max-width: 600px) {
        h1 {
            font-size: 18px;
            gap: 8px;

            svg {
                font-size: 20px;
            }
        }

        .filters {
            width: 100%;
            gap: 8px;
        }

        input[type="date"] {
            flex: 1;
            min-width: 0;
            padding: 8px 10px;
            font-size: 13px;
        }

        button {
            flex: 1;
            padding: 8px 12px;
            font-size: 13px;
        }
    }
`;

const CardsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 20px;
    margin-bottom: 30px;

    @media (max-width: 500px) {
        grid-template-columns: repeat(2, 1fr);
        gap: 12px;
        margin-bottom: 20px;
    }

    @media (max-width: 360px) {
        grid-template-columns: 1fr;
    }
`;

const MetricCard = styled.div`
    background: white;
    padding: 20px;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    display: flex;
    flex-direction: column;
    gap: 8px;

    .icon {
        width: 40px;
        height: 40px;
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 20px;
        margin-bottom: 8px;
    }

    .icon.blue { background: rgba(49, 140, 213, 0.1); color: rgb(49, 140, 213); }
    .icon.red { background: rgba(220, 53, 69, 0.1); color: #dc3545; }
    .icon.green { background: rgba(40, 167, 69, 0.1); color: #28a745; }
    .icon.orange { background: rgba(255, 152, 0, 0.1); color: #ff9800; }

    .label {
        font-size: 14px;
        color: #666;
    }

    .value {
        font-size: 28px;
        font-weight: 700;
        color: #333;
    }

    .sub {
        font-size: 12px;
        color: #999;
    }

    @media (max-width: 500px) {
        padding: 14px;

        .icon {
            width: 32px;
            height: 32px;
            font-size: 16px;
            margin-bottom: 4px;
        }

        .label {
            font-size: 12px;
        }

        .value {
            font-size: 22px;
        }

        .sub {
            font-size: 11px;
        }
    }
`;

const ChartsRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    margin-bottom: 30px;

    @media (max-width: 900px) {
        grid-template-columns: 1fr;
    }

    @media (max-width: 480px) {
        gap: 16px;
        margin-bottom: 20px;
    }
`;

const ChartCard = styled.div`
    background: white;
    padding: 20px;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    overflow: hidden;
    min-width: 0;

    h3 {
        font-size: 16px;
        font-weight: 600;
        color: #333;
        margin-bottom: 20px;
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .chart-container {
        height: 250px;
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;
        width: 100%;

        canvas {
            max-width: 100% !important;
            max-height: 100% !important;
        }
    }

    @media (max-width: 600px) {
        padding: 16px;

        h3 {
            font-size: 14px;
            margin-bottom: 16px;
        }

        .chart-container {
            height: 220px;
        }
    }

    @media (max-width: 400px) {
        padding: 12px;

        .chart-container {
            height: 200px;
        }
    }
`;

const AbandonedListCard = styled.div`
    background: white;
    border-radius: 16px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    overflow: hidden;
    margin-bottom: 20px;

    .abandoned-header {
        padding: 20px 24px;
        border-bottom: 1px solid #e2e8f0;

        h3 {
            font-size: 18px;
            font-weight: 600;
            color: #2d3748;
            margin: 0;
            display: flex;
            align-items: center;
            gap: 10px;
        }
    }

    .abandoned-table-wrapper {
        overflow-x: auto;
    }

    .abandoned-table {
        width: 100%;
        border-collapse: collapse;

        .abandoned-table-header {
            background: linear-gradient(135deg, rgb(49, 140, 213), rgb(79, 172, 254));

            th {
                padding: 16px;
                text-align: left;
                font-weight: 600;
                font-size: 13px;
                color: white;
                border: none;
                white-space: nowrap;

                &:first-child {
                    padding-left: 24px;
                }

                &:last-child {
                    padding-right: 24px;
                    text-align: center;
                }
            }
        }

        .abandoned-table-body {
            tr {
                border-bottom: 1px solid #f1f5f9;
                transition: background-color 0.2s ease;

                &:hover {
                    background: #f8fafc;
                }

                &:last-child {
                    border-bottom: none;
                }

                td {
                    padding: 14px 16px;
                    vertical-align: middle;
                    color: #2d3748;
                    font-size: 14px;

                    &:first-child {
                        padding-left: 24px;
                    }

                    &:last-child {
                        padding-right: 24px;
                        text-align: center;
                    }

                    .cliente-info {
                        display: flex;
                        flex-direction: column;
                        gap: 2px;

                        .nome {
                            font-weight: 600;
                            color: #2d3748;
                        }

                        .telefone {
                            font-size: 12px;
                            color: #718096;
                        }
                    }

                    .abandoned-badge {
                        display: inline-flex;
                        align-items: center;
                        padding: 4px 10px;
                        border-radius: 20px;
                        font-size: 11px;
                        font-weight: 600;

                        &.preco { background: #fef3c7; color: #92400e; }
                        &.taxa_entrega { background: #fee2e2; color: #991b1b; }
                        &.tempo_entrega { background: #ffedd5; color: #9a3412; }
                        &.fechou_aba { background: #d1fae5; color: #065f46; }
                        &.timeout { background: #dbeafe; color: #1e40af; }
                        &.pagamento { background: #ede9fe; color: #5b21b6; }
                        &.outro { background: #f3f4f6; color: #4b5563; }
                        &.default { background: #e5e7eb; color: #374151; }
                    }

                    .valor-highlight {
                        font-weight: 700;
                        color: rgb(49, 140, 213);
                    }

                    .abandoned-actions {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        gap: 8px;

                        .abandoned-action-btn {
                            position: relative;
                            width: 36px;
                            height: 36px;
                            border: none;
                            border-radius: 8px;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            cursor: pointer;
                            transition: all 0.2s ease;

                            &:hover .abandoned-tooltip {
                                opacity: 1;
                                visibility: visible;
                            }

                            &.whatsapp {
                                background: #dcfce7;
                                color: #166534;

                                &:hover {
                                    background: #25d366;
                                    color: white;
                                    transform: scale(1.1);
                                }
                            }

                            &.disabled {
                                background: #f1f5f9;
                                color: #94a3b8;
                                cursor: default;
                                font-size: 10px;
                                width: auto;
                                padding: 0 10px;
                            }
                        }

                        .abandoned-tooltip {
                            position: absolute;
                            bottom: 100%;
                            left: 50%;
                            transform: translateX(-50%);
                            background: #2d3748;
                            color: white;
                            padding: 6px 12px;
                            border-radius: 6px;
                            font-size: 12px;
                            font-weight: 500;
                            white-space: nowrap;
                            opacity: 0;
                            visibility: hidden;
                            transition: all 0.2s ease;
                            margin-bottom: 8px;
                            z-index: 1000;

                            &::after {
                                content: '';
                                position: absolute;
                                top: 100%;
                                left: 50%;
                                transform: translateX(-50%);
                                border: 4px solid transparent;
                                border-top-color: #2d3748;
                            }
                        }
                    }
                }
            }
        }
    }

    .empty {
        text-align: center;
        padding: 60px 40px;
        color: #718096;

        svg {
            color: #cbd5e0;
            margin-bottom: 16px;
        }

        p {
            margin: 8px 0;
        }
    }

    @media (max-width: 768px) {
        .abandoned-table {
            min-width: 600px;
        }
    }
`;

const LoadingContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 300px;
`;

const Abandono = () => {
    const userParse = getStoredUser();

    const empresa = localStorage.getItem('empresa');
    const empresaParse = JSON.parse(empresa);
    const idEmpresa = empresaParse.id_empresa;

    console.log('[ABANDONO] Empresa logada:', empresaParse);
    console.log('[ABANDONO] ID Empresa:', idEmpresa);

    const { sidebar } = useContext(SidebarContext);
    const { t } = useTranslation('reports');

    const [isLoading, setIsLoading] = useState(true);
    const [stats, setStats] = useState({
        metricas: {
            total_carrinhos: 0,
            abandonados: 0,
            finalizados: 0,
            recuperados: 0,
            taxa_abandono: '0%',
            taxa_conversao: '0%',
            taxa_recuperacao: '0%',
            valor_perdido: 0,
            valor_recuperado: 0
        },
        por_motivo: [],
        por_passo: [],
        recuperacao: { recuperados: 0, em_recuperacao: 0 }
    });
    const [abandonados, setAbandonados] = useState([]);
    const [dataInicio, setDataInicio] = useState(nowTz().subtract(30, 'days').format('YYYY-MM-DD'));
    const [dataFim, setDataFim] = useState(nowTz().format('YYYY-MM-DD'));

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const [statsResponse, abandonadosResponse] = await Promise.all([
                getEstatisticasAbandono(idEmpresa, dataInicio, dataFim),
                getCarrinhosAbandonados(idEmpresa, 48, 20)
            ]);

            console.log('[ABANDONO] Stats response:', statsResponse?.data);
            console.log('[ABANDONO] Abandonados response:', abandonadosResponse?.data);

            if (statsResponse?.data?.success) {
                setStats(statsResponse.data);
            }

            if (abandonadosResponse?.data?.success) {
                setAbandonados(abandonadosResponse.data.carrinhos || []);
            }
        } catch (error) {
            console.error('[ABANDONO] Erro ao buscar dados:', error);
            toast.error(t('common.fetchError'));
        } finally {
            setIsLoading(false);
        }
    }, [idEmpresa, dataInicio, dataFim]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleWhatsAppContact = (telefone, nome) => {
        const message = t('abandoned.whatsappMessage', { name: nome ? ` ${nome}` : '' });
        window.open(buildWhatsAppLink(telefone, message), '_blank');
    };

    // Dados do gráfico de motivos
    const motivosData = {
        labels: stats?.por_motivo?.map(m => {
            const labels = {
                'fechou_aba': t('abandoned.reasons.fechou_aba'),
                'timeout': t('abandoned.reasons.timeout'),
                'preco': t('abandoned.reasons.preco'),
                'taxa_entrega': t('abandoned.reasons.taxa_entrega'),
                'tempo_entrega': t('abandoned.reasons.tempo_entrega'),
                'pagamento': t('abandoned.reasons.pagamento'),
                'outro': t('abandoned.reasons.outro')
            };
            return labels[m._id] || m._id;
        }) || [],
        datasets: [{
            data: stats?.por_motivo?.map(m => m.count) || [],
            backgroundColor: [
                '#FF6384',
                '#36A2EB',
                '#FFCE56',
                '#4BC0C0',
                '#9966FF',
                '#FF9F40',
                '#C9CBCF'
            ],
            borderWidth: 0
        }]
    };

    // Dados do gráfico de passos
    const passosData = {
        labels: stats?.por_passo?.map(p => {
            const labels = {
                'adicionou_item': t('abandoned.steps.adicionou_item'),
                'viu_carrinho': t('abandoned.steps.viu_carrinho'),
                'escolheu_entrega': t('abandoned.steps.escolheu_entrega'),
                'confirmou_pedido': t('abandoned.steps.confirmou_pedido'),
                // Mantendo antigos para compatibilidade
                'cardapio': t('abandoned.steps.cardapio'),
                'carrinho': t('abandoned.steps.carrinho'),
                'finalizar_pedido': t('abandoned.steps.finalizar_pedido'),
                'pagamento': t('abandoned.steps.pagamento'),
                'endereco': t('abandoned.steps.endereco')
            };
            return labels[p._id] || p._id || t('abandoned.steps.unknown');
        }) || [],
        datasets: [{
            label: t('abandoned.stepsChartLabel'),
            data: stats?.por_passo?.map(p => p.count) || [],
            backgroundColor: 'rgba(49, 140, 213, 0.7)',
            borderColor: 'rgb(49, 140, 213)',
            borderWidth: 1
        }]
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    padding: 20,
                    font: {
                        size: 12
                    }
                }
            }
        }
    };

    const barOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    stepSize: 1
                }
            }
        }
    };

    return (
        <>
            <PermissionGate permissions={['default']}>
                <Container sidebar={sidebar}>
                    <Header>
                        <div className="page-title-with-icon">
                            <div className="page-icon"><MdOutlineRemoveShoppingCart /></div>
                            <h1 className="page-title">{t('abandoned.title')}</h1>
                        </div>
                        <div className="filters" style={{ marginRight: '50px' }}>
                            <input
                                type="date"
                                value={dataInicio}
                                onChange={(e) => setDataInicio(e.target.value)}
                            />
                            <input
                                type="date"
                                value={dataFim}
                                onChange={(e) => setDataFim(e.target.value)}
                            />
                            <button onClick={fetchData} disabled={isLoading} style={{minHeight: "40px"}}>
                                {isLoading ? t('common.updating') : t('common.update')}
                            </button>
                        </div>
                    </Header>

                    {isLoading ? (
                        <LoadingContainer>
                            <Loading type="spin" color="rgb(49, 140, 213)" height={40} width={40} />
                        </LoadingContainer>
                    ) : (
                        <>
                            <CardsGrid>
                                <MetricCard>
                                    <div className="icon blue">
                                        <FaShoppingCart />
                                    </div>
                                    <span className="label">{t('abandoned.totalCarts')}</span>
                                    <span className="value">{stats?.metricas?.total_carrinhos || 0}</span>
                                    <span className="sub">{t('abandoned.totalCartsSub')}</span>
                                </MetricCard>

                                <MetricCard>
                                    <div className="icon red">
                                        <MdOutlineRemoveShoppingCart />
                                    </div>
                                    <span className="label">{t('abandoned.abandoned')}</span>
                                    <span className="value">{stats?.metricas?.abandonados || 0}</span>
                                    <span className="sub">{t('abandoned.abandonedSub')}</span>
                                </MetricCard>

                                <MetricCard>
                                    <div className="icon green">
                                        <FaChartLine />
                                    </div>
                                    <span className="label">{t('abandoned.completed')}</span>
                                    <span className="value">{stats?.metricas?.finalizados || 0}</span>
                                    <span className="sub">{t('abandoned.completedSub', { rate: stats?.metricas?.taxa_conversao || '0%' })}</span>
                                </MetricCard>

                                <MetricCard>
                                    <div className="icon orange">
                                        <FaMoneyBillWave />
                                    </div>
                                    <span className="label">{t('abandoned.lostValue')}</span>
                                    <span className="value">
                                        {formatPriceWithSymbol(stats?.metricas?.valor_perdido || 0)}
                                    </span>
                                    <span className="sub">
                                        {stats?.metricas?.recuperados > 0
                                            ? t('abandoned.recoveredValue', { value: formatPriceWithSymbol(stats?.metricas?.valor_recuperado || 0) })
                                            : t('abandoned.recoverablePotential')
                                        }
                                    </span>
                                </MetricCard>
                            </CardsGrid>

                            <ChartsRow>
                                <ChartCard>
                                    <h3>
                                        <FaPercentage />
                                        {t('abandoned.reasonsTitle')}
                                    </h3>
                                    <div className="chart-container">
                                        {stats?.por_motivo?.length > 0 ? (
                                            <Doughnut data={motivosData} options={chartOptions} />
                                        ) : (
                                            <span style={{ color: '#999' }}>{t('common.noData')}</span>
                                        )}
                                    </div>
                                </ChartCard>

                                <ChartCard>
                                    <h3>
                                        <FaClock />
                                        {t('abandoned.stepsTitle')}
                                    </h3>
                                    <div className="chart-container">
                                        {stats?.por_passo?.length > 0 ? (
                                            <Bar data={passosData} options={barOptions} />
                                        ) : (
                                            <span style={{ color: '#999' }}>{t('common.noData')}</span>
                                        )}
                                    </div>
                                </ChartCard>
                            </ChartsRow>

                            <AbandonedListCard>
                                <div className="abandoned-header">
                                    <h3>
                                        <FaWhatsapp color="#25d366" />
                                        {t('abandoned.remarketingTitle')}
                                    </h3>
                                </div>
                                {abandonados.length > 0 ? (
                                    <div className="abandoned-table-wrapper">
                                        <table className="abandoned-table">
                                            <thead className="abandoned-table-header">
                                                <tr>
                                                    <th>{t('abandoned.tableHeaders.client')}</th>
                                                    <th>{t('abandoned.tableHeaders.reason')}</th>
                                                    <th>{t('abandoned.tableHeaders.date')}</th>
                                                    <th>{t('abandoned.tableHeaders.value')}</th>
                                                    <th>{t('abandoned.tableHeaders.actions')}</th>
                                                </tr>
                                            </thead>
                                            <tbody className="abandoned-table-body">
                                                {abandonados.map((carrinho, index) => (
                                                    <tr key={index}>
                                                        <td>
                                                            <div className="cliente-info">
                                                                <span className="nome">
                                                                    {carrinho.lead?.nome || t('abandoned.anonymousClient')}
                                                                </span>
                                                                <span className="telefone">
                                                                    {carrinho.lead?.telefone
                                                                        ? carrinho.lead.telefone.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3')
                                                                        : t('abandoned.itemCount', { count: carrinho.itens_count })
                                                                    }
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <span className={`abandoned-badge ${carrinho.abandono_motivo || 'default'}`}>
                                                                {{
                                                                    'fechou_aba': t('abandoned.reasons.fechou_aba'),
                                                                    'timeout': t('abandoned.reasons.timeout'),
                                                                    'preco': t('abandoned.reasons.preco'),
                                                                    'taxa_entrega': t('abandoned.reasons.taxa_entrega'),
                                                                    'tempo_entrega': t('abandoned.reasons.tempo_entrega'),
                                                                    'pagamento': t('abandoned.reasons.pagamento'),
                                                                    'outro': t('abandoned.reasons.outro')
                                                                }[carrinho.abandono_motivo] || t('abandoned.na')}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            {formatDateTz(carrinho.abandonado_at, 'DD/MM HH:mm')}
                                                        </td>
                                                        <td>
                                                            <span className="valor-highlight">
                                                                {formatPriceWithSymbol(carrinho.total || 0)}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <div className="abandoned-actions">
                                                                {carrinho.lead?.telefone ? (
                                                                    <button
                                                                        className="abandoned-action-btn whatsapp"
                                                                        onClick={() => handleWhatsAppContact(carrinho.lead.telefone, carrinho.lead.nome)}
                                                                    >
                                                                        <FaWhatsapp size={16} />
                                                                        <span className="abandoned-tooltip">{t('abandoned.sendWhatsApp')}</span>
                                                                    </button>
                                                                ) : (
                                                                    <span className="abandoned-action-btn disabled">{t('abandoned.anonymous')}</span>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="empty">
                                        <MdOutlineRemoveShoppingCart size={48} />
                                        <p>{t('abandoned.emptyTitle')}</p>
                                        <p style={{ fontSize: 12 }}>{t('abandoned.emptySub')}</p>
                                    </div>
                                )}
                            </AbandonedListCard>
                        </>
                    )}
                </Container>
            </PermissionGate>
        </>
    );
};

export default Abandono;
